import { createSelector } from '@reduxjs/toolkit'
import type { CollapseProps } from 'antd'
import { Alert, Button, Collapse, Popconfirm, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { CardinalApiState, setNewlyCreatedRecoveryKey } from '../../../core/services/auth.api'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import { CustomModal } from '../../common/CustomModal'
import { CopyJSONButton } from '../../common/CopyJSONButton'
import './index.less'
import { useGetPractitionerQuery } from '../../../core/api/practitionerApi'
import { TelecomType } from '@icure/cardinal-sdk'
import { getPractitionerName } from '../../../helpers/practitioner'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    newlyCreatedRecoveryKey: cardinalApi.newlyCreatedRecoveryKey,
    healthcarePartyId: cardinalApi.user?.healthcarePartyId,
  }),
)

export const ModalRecoveryKey = () => {
  const { t } = useTranslation()
  const { newlyCreatedRecoveryKey, healthcarePartyId } = useAppSelector(reduxSelector)
  const dispatch = useAppDispatch()
  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(healthcarePartyId ?? '', { skip: !healthcarePartyId })

  const practitionerEmail = practitioner?.addresses[0].telecoms.find((item) => item.telecomType === TelecomType.Email)?.telecomNumber
  const practitionerName = getPractitionerName(practitioner, 'User')
  const jsonTitle = `RetinoBridge_RecoveryKey_${practitionerName.replace(/\s+/g, '_')}`
  const jsonData = {
    RetinoBridge: {
      accountEmail: practitionerEmail,
      accountName: practitionerName,
      recoveryKey: newlyCreatedRecoveryKey,
    },
  }

  const defaultOnClose = () => dispatch(setNewlyCreatedRecoveryKey({ recoveryKey: undefined }))
  const getCustomFooter = () => {
    return [
      <Popconfirm
        key="confirmClose"
        title={t('user.modalRecoveryKey.popconfirm.title')}
        description={
          <div>
            <p>{t('user.modalRecoveryKey.popconfirm.description1')}</p>
            <p>{t('user.modalRecoveryKey.popconfirm.description2')}</p>
          </div>
        }
        onConfirm={() => defaultOnClose()}
        okText={t('user.modalRecoveryKey.popconfirm.okText')}
        okButtonProps={{ danger: true }}
        cancelText={t('user.modalRecoveryKey.popconfirm.cancelText')}
        placement="topRight"
      >
        <Button key="back">{t('user.modalRecoveryKey.buttons.close')}</Button>
      </Popconfirm>,

      <Button key="submit" type="primary" disabled={isPractitionerFetching}>
        <CopyJSONButton jsonData={jsonData} jsonName={jsonTitle} buttonTitle={t('user.modalRecoveryKey.buttons.downloadKeyFile')} />
      </Button>,
    ]
  }
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: t('user.modalRecoveryKey.collapse.item1.label'),
      children: <p>{t('user.modalRecoveryKey.collapse.item1.text')}</p>,
    },
    {
      key: '2',
      label: t('user.modalRecoveryKey.collapse.item2.label'),
      children: (
        <p>
          {t('user.modalRecoveryKey.collapse.item2.text_part1')}
          <strong>{t('user.modalRecoveryKey.collapse.item2.text_strong')}</strong>
          {t('user.modalRecoveryKey.collapse.item2.text_part2')}
        </p>
      ),
    },
    {
      key: '3',
      label: t('user.modalRecoveryKey.collapse.item3.label'),
      children: <p>{t('user.modalRecoveryKey.collapse.item3.text')}</p>,
    },
    {
      key: '4',
      label: t('user.modalRecoveryKey.collapse.item4.label'),
      children: <p>{t('user.modalRecoveryKey.collapse.item4.text')}</p>,
    },
  ]

  return (
    <CustomModal closable={false} isVisible={!!newlyCreatedRecoveryKey} title={t('user.modalRecoveryKey.title')} customFooter={getCustomFooter()}>
      <div className="modalRecoveryKey">
        <div className="modalRecoveryKey__content">
          <h6>{t('user.modalRecoveryKey.welcome')}</h6>
          <div>
            <p>{t('user.modalRecoveryKey.intro_line1')}</p>
            <p>{t('user.modalRecoveryKey.intro_line2')}</p>
          </div>
        </div>

        <Alert
          type="info"
          message={t('user.modalRecoveryKey.alert_message')}
          description={
            <Typography.Paragraph style={{ margin: 0 }} className="values-token" copyable={{ text: newlyCreatedRecoveryKey }}>
              <strong>{newlyCreatedRecoveryKey?.toUpperCase()}</strong>
            </Typography.Paragraph>
          }
        />
        <Collapse size="small" items={items} />
      </div>
    </CustomModal>
  )
}
